import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Flame, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import { toast } from 'react-toastify';
import { accountsService } from '../../../services/accountsService';
import Breadcrumb from '../../../components/Common/Breadcrumb';

const formatINR = (v) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(v || 0);

const BurnDisciplineReport = () => {
  const navigate = useNavigate();
  const [financialYear, setFinancialYear] = useState('2026-27');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fyOptions = [];
  const y = new Date().getFullYear();
  for (let i = y - 1; i <= y + 2; i++) fyOptions.push(`${i}-${String(i + 1).slice(-2)}`);

  useEffect(() => {
    loadData();
  }, [financialYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      const r = await accountsService.getBurnDisciplineReport({ financialYear });
      if (r?.success) setData(r.data);
      else setData(null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load burn discipline report');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Accounts', path: '/accounts', onClick: () => navigate('/accounts') },
    { label: 'Reports', path: '/accounts?tab=reports', onClick: () => navigate('/accounts?tab=reports') },
    { label: 'Burn Discipline', path: '/accounts/reports/burn-discipline', isCurrent: true }
  ];

  const d = data || {};
  const byCategory = d.byCategory || [];

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '22px', margin: '0 0 4px 0' }}>Burn Discipline Report</h1>
          <p style={{ fontSize: '12px', margin: 0, color: 'var(--color-text-secondary, #5B6478)' }}>FY budget vs actual by category</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <label style={{ fontSize: '13px', fontWeight: 500 }}>Financial Year</label>
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: 120 }}
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
          >
            {fyOptions.map((fy) => (
              <option key={fy} value={fy}>{fy}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</p>
        </div>
      )}

      {!loading && data && (
        <>
          <div className="dashboard-metrics-grid" style={{ marginBottom: '24px' }}>
            <div className="metric-card revenue">
              <div className="metric-header"><div className="metric-icon"><DollarSign size={16} /></div></div>
              <div className="metric-value">{formatINR(d.cumulativeBudget)}</div>
              <div className="metric-title">Cumulative Budget</div>
              <div className="metric-subtitle">FY {d.financialYear} to date</div>
            </div>
            <div className="metric-card churn">
              <div className="metric-header"><div className="metric-icon"><Flame size={16} /></div></div>
              <div className="metric-value">{formatINR(d.cumulativeActual)}</div>
              <div className="metric-title">Cumulative Actual</div>
              <div className="metric-subtitle">Approved burn to date</div>
            </div>
            <div className="metric-card burn">
              <div className="metric-header"><div className="metric-icon"><TrendingUp size={16} /></div></div>
              <div className="metric-value" style={{ color: (d.cumulativeVariancePercent ?? 0) > 0 ? '#dc3545' : '#198754' }}>
                {d.cumulativeVariancePercent != null ? `${(d.cumulativeVariancePercent >= 0 ? '+' : '')}${d.cumulativeVariancePercent}%` : '—'}
              </div>
              <div className="metric-title">Variance %</div>
              <div className="metric-subtitle">Budget vs actual</div>
            </div>
            <div className="metric-card customers">
              <div className="metric-header"><div className="metric-icon"><TrendingDown size={16} /></div></div>
              <div className="metric-value">
                {d.monthOnMonthGrowthPercent != null ? `${(d.monthOnMonthGrowthPercent >= 0 ? '+' : '')}${d.monthOnMonthGrowthPercent}%` : '—'}
              </div>
              <div className="metric-title">Burn Growth %</div>
              <div className="metric-subtitle">vs prior month</div>
            </div>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, padding: '20px 20px 0' }}>
              By Category
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600 }}>Category</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600 }}>Budget To Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600 }}>Actual To Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600 }}>Variance</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: 600 }}>%</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {byCategory.length > 0 ? (
                  byCategory.map((c, i) => {
                    const isOver = c.status === 'OVER';
                    return (
                      <tr key={c.categoryId || i} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: isOver ? 'rgba(220,53,69,0.06)' : undefined }}>
                        <td style={{ padding: '12px 16px', fontWeight: 500 }}>{c.categoryName}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>{formatINR(c.budgetToDate)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>{formatINR(c.actualToDate)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: isOver ? '#dc3545' : '#198754' }}>
                          {(c.variance >= 0 ? '+' : '')}{formatINR(c.variance)}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', color: isOver ? '#dc3545' : '#198754' }}>
                          {c.variancePercent != null ? `${(c.variancePercent >= 0 ? '+' : '')}${c.variancePercent}%` : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <span className={`badge ${isOver ? 'bg-danger' : 'bg-success'}`}>{c.status}</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                      No data. Create and approve an FY budget first.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && !data && (
        <div className="alert alert-info">Select a financial year to view burn discipline report.</div>
      )}
    </div>
  );
};

export default BurnDisciplineReport;
